import {
  CreditProcessorGetMetricsResponseType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { Dialog, DialogNavigation } from "../../../components/modal/Dialog";
import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  calculateLineItemTotal,
  calculateItemTotal,
  calculateVatTotal,
  calculateTotal,
  formatCurrency,
} from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { useCreditProcessorIssueCreditNote } from "../../../hooks/api/v2/creditProcessor/useCreditProcessorIssueCreditNote";
import { Alert } from "../../../components/alert/Alert";
import { useToast } from "../../../components/toast/useToast.shared";
import { Toast } from "../../../components/toast/Toast";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useCreditProcessorGetMetrics } from "../../../hooks/api/v2/creditProcessor/useCreditProcessorGetMetrics";
import { Icon } from "../../../components";
import { useMediaQuery } from "usehooks-ts";
import {
  Button,
  IconType,
  InputCheckbox,
  InputCurrency,
} from "@sellar-io/components";
type CreditNoteDialogActiveProps = {
  active: boolean;
  onDismiss: () => void;
  order: OrderGetResponseType;
};

const creditNoteMemoLength = 240;

export const CreateCreditNoteDialog = ({
  active,
  order,
  onDismiss,
}: CreditNoteDialogActiveProps) => {
  const { toast } = useToast();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const issueCreditNoteMutation = useCreditProcessorIssueCreditNote();
  const availableCreditQuery = useCreditProcessorGetMetrics(order.id, {
    enabled: Boolean(active && order.id > 0),
  });
  const [errorMessage, setErrorMessage] = useState("");
  const initialOrderItems = order.OrderItems.map(mapToBaseCreditLineItem).map(
    (item) => ({
      ...item,
      selected: false,
      creditAmount: 0,
    }),
  );
  const initialCreditLineItems: CreditLineItem[] = [
    ...initialOrderItems,
    order.deliveryCharge > 0 && {
      id: "delivery",
      selected: false,
      creditAmount: 0,
      quantity: 1,
      name: "Delivery Charge",
      unitPrice: order.deliveryCharge,
      subtotal: order.deliveryCharge,
    },
  ].filter(Boolean);
  const [creditLineItems, setCreditLineItems] = useState<CreditLineItem[]>(
    initialCreditLineItems,
  );
  const [creditNoteMemo, setCreditNoteMemo] = useState("");

  const handleDismiss = () => {
    setCreditLineItems(initialCreditLineItems);
    setErrorMessage("");
    onDismiss();
    setCreditNoteMemo("");
  };

  const handleIssueCreditNote = ({
    orderId,
    creditLineItems,
    vatPercentage,
  }: {
    orderId: number;
    creditLineItems: CreditLineItem[];
    vatPercentage: number;
  }) => {
    const creditLineItemsForAPI = creditLineItems
      .filter((item) => item.selected)
      .map((item) => {
        const total = calculateLineItemTotal(
          { price: item.creditAmount, quantity: 1 },
          vatPercentage,
        );
        return {
          orderItemId: item.orderItemId,
          creditAmountSubtotal: item.creditAmount,
          vatPercentage,
          description: item.name,
          creditAmountTotal: total,
        };
      });

    const formattedCreditNoteMemo = creditNoteMemo.trim();

    issueCreditNoteMutation.mutate(
      {
        orderId,
        items: creditLineItemsForAPI,
        memo: formattedCreditNoteMemo ? formattedCreditNoteMemo : null,
      },
      {
        onSuccess: (response) => {
          const message = `Your credit note (${response.customId}) for the amount of ${formatCurrency(response.amount)} has been issued.`;
          toast({
            placement: "bottom",
            render: () => <Toast icon="CHECK">{message}</Toast>,
          });
          handleDismiss();
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  const hasSelectedLineItems = creditLineItems.some((item) => item.selected);

  const hasInvalidLineItems = creditLineItems
    .filter((item) => item.selected)
    .some(
      (item) => item.creditAmount > item.subtotal || item.creditAmount <= 0,
    );

  const creditOrderItems = creditLineItems
    .filter((item) => item.selected)
    .map((item) => ({ price: item.creditAmount, quantity: 1 }));
  const creditAmountSubtotal = calculateItemTotal(creditOrderItems);
  const creditPricing = {
    orderItems: creditOrderItems,
    deliveryCharge: 0,
    discountAmount: 0,
    vatPercentage: order.vatPercentage,
  };

  const creditAmountAvailable = availableCreditQuery.data?.availableCredit ?? 0;

  const creditAmountVatTotal = calculateVatTotal(creditPricing);

  const creditAmountTotal = calculateTotal(creditPricing);

  const isIssuingCreditNote = issueCreditNoteMutation.isPending;

  const isCurrentCreditAmountValid = creditAmountTotal <= creditAmountAvailable;

  const canIssueCreditNote =
    hasSelectedLineItems &&
    creditAmountTotal > 0 &&
    isCurrentCreditAmountValid &&
    !hasInvalidLineItems;

  const orderItemsAvailableCredit = availableCreditQuery.data?.orderItems ?? [];
  const orderItemsAvailableCreditLookupMap = new Map(
    orderItemsAvailableCredit.map((item) => [item.id, item]),
  );

  return (
    <Dialog active={active} onDismiss={handleDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={handleDismiss}
        title="Create credit note"
      />
      <div className="flex flex-col gap-4 overflow-y-hidden pb-4 text-zinc-700">
        <p className="text-center text-zinc-500">
          Select relevant order item(s) below
        </p>
        {errorMessage && (
          <Alert type="error" message={errorMessage} className="w-full" />
        )}
        <div className="flex flex-col gap-4 overflow-y-auto px-4">
          <CreditNoteLineItemList
            header={
              isDesktop && (
                <div className="grid grid-cols-7 gap-x-4 font-semibold">
                  <div className="col-span-3">Order items</div>
                  <div className="col-span-1 text-center">Unit price</div>
                  <div className="col-span-1 text-center">Subtotal</div>
                  <div className="col-span-2 text-center">Credit amount</div>
                </div>
              )
            }
            renderListItem={(renderProps) => {
              if (isDesktop) {
                return (
                  <CreditNoteLineItemForDesktop
                    {...renderProps}
                    setItems={setCreditLineItems}
                    isIssuingCreditNote={isIssuingCreditNote}
                  />
                );
              }
              return (
                <CreditNoteLineItemForMobile
                  {...renderProps}
                  setItems={setCreditLineItems}
                  isIssuingCreditNote={isIssuingCreditNote}
                />
              );
            }}
            items={creditLineItems}
            orderItemsAvailableCreditLookupMap={
              orderItemsAvailableCreditLookupMap
            }
          />
          <CreditNoteSummary
            creditAmountSubtotal={creditAmountSubtotal}
            creditAmountAvailable={creditAmountAvailable}
            creditAmountVatTotal={creditAmountVatTotal}
            creditAmountTotal={creditAmountTotal}
            creditAmountVatPercentage={order.vatPercentage}
          />
          <div className="flex flex-col gap-2">
            <InputContainer>
              <InputText
                type="text"
                maxLength={creditNoteMemoLength}
                value={creditNoteMemo}
                onChange={(event) => setCreditNoteMemo(event.target.value)}
                placeholder="Add a memo"
                disabled={isIssuingCreditNote}
              />
            </InputContainer>
            <span className="text-end text-sm text-zinc-400">{`${creditNoteMemo.length} / ${creditNoteMemoLength}`}</span>
          </div>
        </div>
        <div className="flex w-full justify-end gap-2 px-4">
          <Button onClick={handleDismiss} type="button" color="primary-dark">
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            loading={isIssuingCreditNote}
            onClick={() => {
              setErrorMessage("");
              handleIssueCreditNote({
                orderId: order.id,
                creditLineItems,
                vatPercentage: order.vatPercentage,
              });
            }}
            disabled={!canIssueCreditNote || isIssuingCreditNote}
          >
            Issue Credit Note
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

type CreditNoteLineItemListProps = {
  items: CreditLineItem[];
  renderListItem: ({
    item,
    label,
    isCreditAmountInvalid,
  }: {
    item: CreditLineItem;
    label: string;
    isCreditAmountInvalid: boolean;
  }) => ReactNode;
  orderItemsAvailableCreditLookupMap: Map<
    number,
    CreditProcessorGetMetricsResponseType["orderItems"][number]
  >;
  header: ReactNode;
};
const CreditNoteLineItemList = ({
  items,
  orderItemsAvailableCreditLookupMap,
  header,
  renderListItem,
}: CreditNoteLineItemListProps) => {
  return (
    <div className="flex w-full flex-col gap-4">
      {header}
      <div className="flex flex-col gap-y-4">
        {items.map((item) => {
          const hasExceededSubtotal =
            item.creditAmount > item.subtotal && item.selected;

          const hasCreditAmountLessThanOrEqualToZero =
            item.creditAmount <= 0 && item.selected;

          const existingCreditForLineItem = item.orderItemId
            ? orderItemsAvailableCreditLookupMap.get(item.orderItemId)
            : undefined;

          const hasExistingCreditLineItem =
            Boolean(existingCreditForLineItem) && item.selected;

          const existingCreditAvailableForLineItem =
            existingCreditForLineItem?.availableCredit ?? item.subtotal;

          const hasNoAvailableCredit =
            item.selected &&
            Math.max(0, existingCreditAvailableForLineItem) <= 0;

          const label = `${item.quantity} x ${item.name}`;
          const isCreditAmountInvalid =
            hasExceededSubtotal || hasNoAvailableCredit;

          return (
            <div className="flex flex-col gap-y-2" key={item.id}>
              {renderListItem({ item, label, isCreditAmountInvalid })}
              {hasExceededSubtotal && (
                <CustomLineItemAlert
                  type="error"
                  message="You cannot exceed the subtotal for this line item"
                />
              )}
              {hasExistingCreditLineItem && (
                <CustomLineItemAlert
                  type="information"
                  message={`You have ${formatCurrency(existingCreditAvailableForLineItem)} (before tax) available credit for this line item`}
                />
              )}
              {Boolean(
                !hasExistingCreditLineItem &&
                hasCreditAmountLessThanOrEqualToZero,
              ) && (
                <CustomLineItemAlert
                  type="information"
                  message="Your credit amount must be greater than 0"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CreditNoteItemName = ({
  id,
  name,
  checked,
  disabled,
  onToggle,
}: {
  id: string;
  name: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: (id: string) => void;
}) => {
  return (
    <label
      htmlFor={`credit-line-item-name-${id}`}
      className={twMerge(
        "flex cursor-pointer items-center gap-2 rounded-md border border-zinc-200 p-2",
        checked && "bg-zinc-100",
      )}
    >
      <InputCheckbox
        id={`credit-line-item-name-${id}`}
        checked={checked}
        disabled={disabled}
        type="checkbox"
        onClick={() => {
          onToggle(id);
        }}
        className="h-4 min-h-4 w-4 min-w-4 rounded-md border border-zinc-200"
      />
      <span>{name}</span>
    </label>
  );
};

const CreditNoteAmount = ({
  id,
  disabled,
  isInvalid,
  onChange,
}: {
  id: string;
  disabled: boolean;
  isInvalid: boolean;
  onChange: (props: { id: string; amount: number }) => void;
}) => {
  return (
    <InputContainer
      className={twMerge(
        "flex max-w-32 rounded-md border border-zinc-200 text-end",
        isInvalid && "border-red-500",
      )}
    >
      <InputCurrency
        showPrefix
        allowNegative
        disabled={disabled}
        className="text-right placeholder:text-zinc-300"
        placeholder="0.00"
        onChange={(event) => {
          const value = event.target.value.trim();

          // If input is empty, treat as 0
          if (value === "") {
            onChange({ id, amount: 0 });
            return;
          }

          // Convert to number safely
          const amountFloat = parseFloat(value);

          if (isNaN(amountFloat)) {
            onChange({ id, amount: 0 });
            return;
          }

          // Convert to pence by scaling and rounding
          const amountInPence = Math.round(
            Number(amountFloat.toFixed(2)) * 100,
          );

          onChange({ id, amount: amountInPence });
        }}
      />
    </InputContainer>
  );
};

const CreditNoteSummary = ({
  creditAmountAvailable,
  creditAmountSubtotal,
  creditAmountTotal,
  creditAmountVatTotal,
  creditAmountVatPercentage,
}: {
  creditAmountAvailable: number;
  creditAmountTotal: number;
  creditAmountSubtotal: number;
  creditAmountVatTotal: number;
  creditAmountVatPercentage: number;
}) => {
  return (
    <div className="flex w-full flex-col gap-2 rounded-md bg-gray-100 p-4">
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>{formatCurrency(creditAmountSubtotal)}</span>
      </div>
      <div className="flex justify-between">
        <span>VAT ({`${creditAmountVatPercentage}%`})</span>
        <span>{formatCurrency(creditAmountVatTotal)}</span>
      </div>
      <hr />
      <div className="flex justify-between font-semibold">
        <span>Total amount to credit</span>
        <span>{formatCurrency(creditAmountTotal)}</span>
      </div>
      <div className="flex justify-between">
        <span>Available credit</span>
        <span>{formatCurrency(creditAmountAvailable)}</span>
      </div>
    </div>
  );
};

type BaseCreditLineItem = {
  id: string;
  orderItemId?: number;
  name: string;
  unitPrice: number;
  quantity: number;
  subtotal: number;
};

interface CreditLineItem extends BaseCreditLineItem {
  selected: boolean;
  creditAmount: number;
}

const mapToBaseCreditLineItem = (
  item: OrderGetResponseType["OrderItems"][number],
): BaseCreditLineItem => {
  const subtotal = item.quantity * item.price;
  return {
    id: `${item.id}`,
    orderItemId: item.id,
    name: item.Product.name || "Item",
    quantity: item.quantity,
    unitPrice: item.price,
    subtotal,
  };
};

type CustomLineItemAlertProps = {
  message: string;
  type: "information" | "error";
};
const CustomLineItemAlert = ({ type, message }: CustomLineItemAlertProps) => {
  const getStyle = (
    type: CustomLineItemAlertProps["type"],
  ): { iconColor: string; textColor: string; iconType: IconType } => {
    switch (type) {
      case "information":
        return {
          iconColor: "text-blue-800",
          textColor: "text-zinc-700",
          iconType: "INFORMATION",
        };
      case "error":
        return {
          iconColor: "text-red-600",
          textColor: "text-red-600",
          iconType: "ALERT_FILLED",
        };
      default:
        return { iconColor: "", textColor: "", iconType: "INFORMATION" };
    }
  };

  const style = getStyle(type);

  return (
    <div className="flex flex-1 gap-1">
      <div>
        <Icon
          icon={style.iconType}
          className={twMerge(style.iconColor, "h-4 w-4")}
          size={0.8}
        />
      </div>
      <p className={twMerge(style.textColor, "text-sm")}>{message}</p>
    </div>
  );
};

type CreditNoteLineItemProps = {
  item: CreditLineItem;
  label: string;
  setItems: Dispatch<SetStateAction<CreditLineItem[]>>;
  isCreditAmountInvalid: boolean;
  isIssuingCreditNote: boolean;
};

const CreditNoteLineItemForDesktop = ({
  item,
  label,
  isCreditAmountInvalid,
  isIssuingCreditNote,
  setItems,
}: CreditNoteLineItemProps) => {
  return (
    <div className="grid grid-cols-7 gap-x-4">
      <div className="col-span-3 flex flex-col justify-center">
        <CreditNoteItemName
          id={item.id}
          name={label}
          checked={item.selected}
          onToggle={(id) => {
            setItems((prevItems) => {
              return prevItems.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    selected: !item.selected,
                  };
                }
                return item;
              });
            });
          }}
        />
      </div>
      <div className="col-span-1 flex flex-col items-center justify-center">
        {formatCurrency(item.unitPrice)}
      </div>
      <div className="col-span-1 flex flex-col items-center justify-center">
        {formatCurrency(item.subtotal)}
      </div>
      <div className="col-span-2 flex flex-col items-center justify-center">
        <CreditNoteAmount
          id={item.id}
          isInvalid={isCreditAmountInvalid}
          disabled={!item.selected || isIssuingCreditNote}
          onChange={({ id, amount }) => {
            setItems((prevItems) => {
              return prevItems.map((item) => {
                if (item.id === id) {
                  return { ...item, creditAmount: amount };
                }
                return item;
              });
            });
          }}
        />
      </div>
    </div>
  );
};

const CreditNoteLineItemForMobile = ({
  item,
  label,
  isCreditAmountInvalid,
  isIssuingCreditNote,
  setItems,
}: CreditNoteLineItemProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="font-semibold">Order item</span>
        <CreditNoteItemName
          id={item.id}
          name={label}
          checked={item.selected}
          onToggle={(id) => {
            setItems((prevItems) => {
              return prevItems.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    selected: !item.selected,
                  };
                }
                return item;
              });
            });
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="font-semibold">Unit price</span>
        <span> {formatCurrency(item.unitPrice)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-semibold">Subtotal</span>
        <span> {formatCurrency(item.subtotal)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="w-1/2 font-semibold">Credit amount</span>
        <CreditNoteAmount
          id={item.id}
          isInvalid={isCreditAmountInvalid}
          disabled={!item.selected || isIssuingCreditNote}
          onChange={({ id, amount }) => {
            setItems((prevItems) => {
              return prevItems.map((item) => {
                if (item.id === id) {
                  return { ...item, creditAmount: amount };
                }
                return item;
              });
            });
          }}
        />
      </div>
    </div>
  );
};
